import { AfterContentInit,Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { Message } from "@bbwt/classes";
import { FileDetails } from "@main/file-storage";
import { DocumentService } from "@project/services/document.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { FormButtonsConfig } from ".";
import { FileUploadControlComponent } from "@mipro/custom-controls/file-upload.control";
import { CustomFileUploadFn } from "@mipro/classes/custom-file-upload-fn";

@Component({
    selector: "m-documents-dialog",
    template: `
        <p-dialog
            [(visible)]="visible"
            [modal]="modal"
            (onHide)="_onHide($event)"
            [closable]="closable"
            [draggable]="draggable"
            [style]="style"
            appendTo="body"
            [header]="header">
            <div class="date-selector">
                <m-parent-restricted-select-date
                    *ngIf="showDateSelector"
                    name="uploadDateTime"
                    [showValue]="true"
                    [allowTyping]="true"
                    [(ngModel)]="selectedDate"
                    [maxDate]="maxDate"
                    [minDate]="minDate"
                    [parentDate]="parentDate"
                    (onChange)="onDateChanged($event)">
                    <ng-template pTemplate="calendarHeader">
                        <div class="calendar-parent-header">
                            <span>{{ parentEntityName }} Created: {{ minDate | date : "dd/MM/yyyy" }}</span>
                        </div>
                    </ng-template>
                </m-parent-restricted-select-date>
                <div class="p-error" *ngIf="!validDate">
                    <small [style.display]="'block'"> The selected date is invalid </small>
                </div>
            </div>

            <div class="pt-2">
                <m-fileUpload
                    [(ngModel)]="documents"
                    (checkboxChange)="onCheckboxChange($event)"
                    [showDateSelector]="false"
                    [(selectedDate)]="selectedDate"
                    [fileLimit]="fileLimit"
                    [maxDate]="maxDate"
                    [minDate]="minDate"
                    [parentDate]="parentDate"
                    [edit]="edit"
                    [enableDuplicateCheck]="enableDuplicateCheck"
                    [showPrivateCheckboxes]="showPrivateCheckboxes"
                    [customFileUploadMethod]="customFileUploadMethod"
                    (onDateChange)="onDateChanged($event)"
                    (noFileError)="onFileError($event)"
                    [parentEntityName]="parentEntityName">
                </m-fileUpload>
            </div>
            <form-buttons [disabled]="editButtonDisableCheckPassed ||!validDate || saving || (!edit && uploadDisabled)"
                          [cancelDisabled]="saving"
                          [config]="buttonsConfig">
            </form-buttons>
        </p-dialog>
    `,
    styles: [
        `
            .date-selector {
                margin: 10px 0 0 18px;
                display: block;
                max-width: 200px;
            }
        `
    ]
})
export class DocumentsDialogComponent implements AfterContentInit{
    private _visible = false;
    @ViewChild(FileUploadControlComponent, { static: false }) fileUploadComponent: FileUploadControlComponent;

    documents: FileDetails[];
    uploadDisabled: boolean = true;
    private: boolean;
    deletingUploaded = false;
    saving = false;
    localDocuments: FileDetails[];

    editButtonDisableCheckPassed: boolean = false;

    @Input() get visible() {
        return this._visible;
    }
    set visible(visible: boolean) {
        this._visible = visible;
        this.visibleChange.emit(visible);
    }

    @Input() modal = true;
    @Input() closable = true;
    @Input() draggable = false;
    @Input() edit = false;
    @Input() header = "Add Document";
    @Input() style: any = { "max-width": "600px", width: "100%" };
    @Input() clearDocumentsOnHide = true;
    @Input() clearDocumentsOnCancel = true;
    // We can remove this if we allow user to fully edit existing document
    // Currently it's only allowed to edit Selected Date
    @Input() disableFileUpload = false;

    @Input() showDateSelector: boolean = false;
    @Input() showPrivateCheckboxes: boolean = false;
    @Input() enableDuplicateCheck: boolean = false;
    @Input() fileLimit: number;
    @Input() selectedDate: Date = new Date();
    @Input() defaultSelectedDate: Date;
    @Input() maxDate: Date = null;
    @Input() minDate: Date = null;
    @Input() parentDate: Date = null;
    @Input() parentEntityName: string;

    @Input() customFileUploadMethod?: CustomFileUploadFn = undefined;

    @Output() readonly visibleChange = new EventEmitter<boolean>();
    @Output() readonly onHide = new EventEmitter<any>();
    @Output() readonly onAccept = new EventEmitter<{ rawFiles: any[], files: FileDetails[]; createDateTime?: Date, private?: boolean }>();
    @Output() readonly onCancel = new EventEmitter<void>();

    buttonsConfig: FormButtonsConfig = <FormButtonsConfig>{
        saveLabel: this.edit ? "Update" : "Upload",
        saveFunc: () => this.save(),
        cancelFunc: () => this.cancel(),
        justifyContent: "end"
    }

    constructor(
        private documentService: DocumentService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService
    ) {}

    ngAfterContentInit(): void {
        this.buttonsConfig = <FormButtonsConfig>{
            saveLabel: this.edit ? "Update" : "Upload",
            saveFunc: () => this.save(),
            cancelFunc: () => this.cancel(),
            justifyContent: "end",
            saveDisabled: this.edit
        }

    }

    _onHide(e: any) {
        if (this.clearDocumentsOnHide) {
            this.resetDocuments();
        }
        this.onHide.emit(e);
    }

    editButtonDisableCheck(): boolean {

        if (!this.localDocuments || !this.fileUploadComponent || !this.fileUploadComponent.pFileUpload || !this.fileUploadComponent.pFileUpload.files) {
            return false;
        }
        const result: boolean = this.compareRawsSaved(this.localDocuments, this.fileUploadComponent.pFileUpload?.files);

        if (this.edit && this.defaultSelectedDate.toDateString() == this.selectedDate.toDateString() && result) {
            return true;
        }

        return false;
    }

    compareRawsSaved(documents: FileDetails[], files: any[]): boolean {
        if (documents.length !== files.length) {
            return false;
        }

        for (let doc of documents) {
            const correspondingFile = files.find(file => file.id === doc.id);
            if (!correspondingFile || correspondingFile.private !== doc.private) {
                return false;
            }
        }

        return true;
    }

    writeValue(documents: FileDetails[]) {
        this.localDocuments = documents;
        this.fileUploadComponent.writeValue(documents);
        this.editButtonDisableCheckPassed = true;
    }

    onFileError(noFileError : boolean) {
        this.uploadDisabled = !noFileError;
    }

    private async save() {
        if (!this.fileUploadComponent) return;

        if (!this.edit) {
            const selectedCount = this.fileUploadComponent.pFileUpload.files.length;
            const uploadedBefore = this.documents?.length ?? 0;
            await this.uploadFiles(this.fileUploadComponent.pFileUpload.files);

            if (selectedCount > 0 && (this.documents?.length ?? 0) === uploadedBefore) {
                return;
            }
        }

        this.accept();
    }

    private accept() {
        this.onAccept.emit({ rawFiles: this.fileUploadComponent.pFileUpload.files, files: this.documents, createDateTime: this.selectedDate, private: this.private });
        this.fileUploadComponent.documentType = undefined;
        this.visible = false;
        this.uploadDisabled = true;

        if (!this.edit) {
            this.selectedDate = new Date();
        }

        setTimeout(() => {
            this.fileUploadComponent.selectedDocumentTypeComponent?.el?.nativeElement?.classList?.remove("ng-invalid");
        }, 100);
    }

    private cancel() {
        if (!this.edit && this.fileUploadComponent.pFileUpload.files?.length > 0) {
            this.confirmationService.confirm({
                message: "Are you sure you want to delete this entry?",
                accept: () => this.closeAndCancel(),
                reject: () => this.closeAndCancel()
            });
        } else {
            this.closeAndCancel();
        }
    }

    async uploadFiles(files: File[]): Promise<void> {
        this.saving = true;
        if (this.fileUploadComponent) {
            await this.fileUploadComponent.onUpload(files);
        }
        this.saving = false;
    }

    private closeAndCancel() {
        this.onCancel.emit();
        this.fileUploadComponent.documentType = undefined;
        this.selectedDate = this.defaultSelectedDate ?? new Date();
        this.visible = false;
        this.uploadDisabled = true;

        setTimeout(() => {
            this.fileUploadComponent.selectedDocumentTypeComponent?.el?.nativeElement?.classList?.remove("ng-invalid");
        }, 100);
    }

    private async confirmDeleteUploaded() {
        this.deletingUploaded = true;
        try {
            for (const f of this.documents) {
                await this.documentService.delete(f.id, { showSuccessMessage: false });
            }
            this.resetDocuments();
            this.messageService.add(Message.Success("Files deleted successfully.", this.header));
            this.closeAndCancel();
        } finally {
            this.deletingUploaded = false;
        }
    }

    private resetDocuments() {
        this.documents = [];
        this.private = false;
    }

    onCheckboxChange(checked: boolean) {
         this.editButtonDisableCheckPassed = this.editButtonDisableCheck();
    }

    onDateChanged(date: Date) {
        this.selectedDate = date;
        this.editButtonDisableCheckPassed = this.editButtonDisableCheck();
    }

    get validDate() {
        return this.selectedDate instanceof Date;
    }
}
